import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, ButtonBase, Link, Stack, Typography } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { PlainTextParagraphs } from '../../../../components/PlainTextParagraphs'
import { sanitizeHtml } from '../../../../helpers/dom'

export interface TrendsChartLegendItem {
  id: string
  label: string
  // Resolved series color — the legend never derives colors itself so the swatch
  // can't drift from the plotted line.
  color: string
  // Localized, unsanitized glossary copy shown on hover. Sanitized here, per
  // item, rather than up front by the caller.
  description?: string
  // Blueprint family copy is authored as HTML; event type copy is plain text in
  // the language map and must not be injected as markup.
  isPlainTextDescription?: boolean
}

interface TrendsChartLegendProps {
  items: TrendsChartLegendItem[]
  // Ids to isolate. Empty means "no isolation" — every series is plotted, which
  // is why this can't be modelled as the inverse (a hidden set).
  highlightedIds: string[]
  onToggle: (id: string) => void
  onClearHighlight: () => void
}

interface TrendsChartLegendRowProps {
  item: TrendsChartLegendItem
  isHighlighted: boolean
  isDimmed: boolean
  onToggle: (id: string) => void
}

function TrendsChartLegendRow({ item, isHighlighted, isDimmed, onToggle }: TrendsChartLegendRowProps) {
  // Editorial glossary copy is rich HTML; sanitizing is expensive, so it happens
  // per item and only when there is copy to render.
  const sanitizedDescription = useMemo(
    () => (item.description && !item.isPlainTextDescription ? sanitizeHtml(item.description) : ''),
    [item.description, item.isPlainTextDescription]
  )

  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {item.label}
      </Typography>
      {item.description && item.isPlainTextDescription ? (
        <PlainTextParagraphs text={item.description} sx={{ overflowWrap: 'anywhere' }} />
      ) : sanitizedDescription ? (
        <Typography variant="body2" component="div" sx={{ '& p': { m: 0 } }} dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
      ) : null}
    </Box>
  )

  return (
    <Box component="li" sx={{ minWidth: 0 }}>
      <GRTooltip content={tooltipContent} styles={{ display: 'block', minWidth: 0 }}>
        <ButtonBase
          onClick={() => onToggle(item.id)}
          aria-pressed={isHighlighted}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            gap: 1,
            px: 0.75,
            py: 0.5,
            borderRadius: 1,
            textAlign: 'left',
            opacity: isDimmed ? 0.45 : 1,
            transition: 'opacity 150ms ease-in-out',
            '&:hover': { bgcolor: 'action.hover' },
            // ButtonBase resets the outline and draws no ripple on focus without `focusRipple`.
            '&.Mui-focusVisible': { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
          }}
        >
          <Box sx={{ width: 14, height: 3, borderRadius: 1, flexShrink: 0, bgcolor: item.color }} />
          <Typography variant="caption" noWrap sx={{ minWidth: 0, fontWeight: isHighlighted ? 700 : 400, color: 'text.primary' }}>
            {item.label}
          </Typography>
        </ButtonBase>
      </GRTooltip>
    </Box>
  )
}

/**
 * Vertical legend for the trends line charts, replacing Chart.js's built-in one.
 *
 * Two reasons it exists: the built-in legend wraps into a horizontal block that
 * truncates unpredictably with ten long blueprint names, and its click handler
 * hides the clicked series — the opposite of what analysts want. Here a click
 * *isolates*: with nothing picked every line is plotted, picking items narrows
 * the chart to those, and clicking a picked item again drops it from the set.
 *
 * Hovering an item shows the same glossary description as the catalogue tree, so
 * "what is a Pick One Offer?" is answerable without leaving the chart.
 *
 * A chart with a single series renders no legend: isolating one line out of one
 * changes nothing, so the column would be an empty 200px gutter offering a
 * pointless control. That series is still named by the chart's hover tooltip.
 */
export const TrendsChartLegend = React.memo(function TrendsChartLegend({ items, highlightedIds, onToggle, onClearHighlight }: TrendsChartLegendProps) {
  const { t } = useTranslation()

  const highlighted = useMemo(() => new Set(highlightedIds), [highlightedIds])
  const hasHighlight = highlighted.size > 0

  if (items.length < 2) return null

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0, width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {t('market-analytics:chart_legend_hint')}
        </Typography>
        {hasHighlight ? (
          <Link component="button" type="button" onClick={onClearHighlight} variant="caption" underline="hover" sx={{ fontWeight: 700, flexShrink: 0 }}>
            {t('market-analytics:chart_legend_show_all')}
          </Link>
        ) : null}
      </Stack>
      <Stack
        component="ul"
        spacing={0.25}
        aria-label={t('market-analytics:chart_legend_aria')}
        sx={{ listStyle: 'none', m: 0, p: 0, minWidth: 0, maxHeight: 320, overflowY: 'auto' }}
      >
        {items.map((item) => (
          <TrendsChartLegendRow
            key={item.id}
            item={item}
            isHighlighted={highlighted.has(item.id)}
            isDimmed={hasHighlight && !highlighted.has(item.id)}
            onToggle={onToggle}
          />
        ))}
      </Stack>
    </Stack>
  )
})

TrendsChartLegend.displayName = 'TrendsChartLegend'
